/**
 * Putting a fighter from the ladder into the slot the game fights in.
 *
 * There are two ideas of "a fighter" in this project and they are not the same
 * object. The ladder's is a **roster entry** — a name, some styles, a tier, a
 * stat spread — and the game's is a **combatant**, a rig with a state machine
 * and a health model that lives in the scene for the whole session. A round
 * does not build a new one of those; it dresses the one that is already there.
 *
 * This is the seam, and it is written as a *table* rather than as four
 * assignments on purpose. A roster field that nobody wears is the same bug
 * that dropped `unblockable` for three build steps (gotcha 29): the data says
 * a fighter is quick and the game has never read it. So every field a roster
 * entry can carry is either in `WORN` — with the handle that takes it — or in
 * `NOT_WORN` with a reason, and the test fails on a field that is in neither.
 *
 * **One way a fighter gets dressed, not two.** The player is a roster entry with
 * a flag on it, exactly as they are on the ladder itself — so they come through
 * here too, and the one thing that differs between the two slots is written down
 * in `NOT_WORN` rather than being a second function somebody has to keep in step
 * with this one.
 *
 * Pure: it is handed two duck-typed handles and touches nothing else. `fighter`
 * needs `setName`, `setStyles`, `setSpread`, `setTint` and `wear`; `brain` needs `retier`.
 */

import { looksOf } from './bank.js';

/**
 * Fields that do not reach a fighter, who does not wear them, and why.
 *
 * `who` is `'anybody'` for a field no slot ever wears, or `'player'` for one
 * that only the player's own entry leaves behind. A field can appear here *and*
 * in `WORN` — `tier` does — and that is the point: the table is about who wears
 * a field rather than about whether the field is ever worn.
 */
export const NOT_WORN = Object.freeze({
  /** The ladder's key. The combatant's `id` is the *slot* and must not move. */
  id: { who: 'anybody', why: 'the ladder keys on it; the combatant id is the slot' },
  /** Which entry is the player. Decided before anybody is booked. */
  player: { who: 'anybody', why: 'the campaign reads it, not the fighter' },
  /**
   * Where they are from. Identity rather than ability: it is on the card and it
   * is in what the crowd shouts, and there is nothing on a combatant it could
   * be put into. The screens read it off the roster entry, like the rank.
   */
  borough: { who: 'anybody', why: 'the screens read it off the ladder; a fighter has nowhere to wear it' },
  /**
   * The venue they fight out of. It decides *where* a fight is held — which
   * arena loads — and that is the arena host's business, read off the card,
   * not anything a body in the slot could wear. See `venues.js`.
   */
  home: { who: 'anybody', why: 'it picks the arena a card is fought in; the slot has nowhere to wear it' },
  /**
   * Whether this entry is the fighter who arrives partway through the career.
   * A fact about the *ladder* — when somebody gets a rank — and by the time
   * anybody is booked they have one, so there is nothing left for a fighter to
   * wear. See `validateDebut` in `ladder.js`.
   */
  debut: { who: 'anybody', why: 'the ladder decides when they get a rank; by booking, they have one' },
  /**
   * Whether a card may say what they fight before they walk out. A rule about
   * what a *screen* is allowed to print, and the fighter it describes is
   * wearing the styles either way — concealing them from a combatant would
   * conceal them from the strike layer too.
   */
  hidden: { who: 'anybody', why: 'the offer screen reads it; a fighter still fights the styles' },
  /**
   * The player's difficulty, which is not a thing. A tier tells an AI how long
   * to take to see a punch and how often to answer it, and nothing is driving
   * the player but a person — there is no brain in that slot to re-tier, and a
   * number describing how good they are at their own game would be a fiction.
   */
  tier: { who: 'player', why: 'no AI drives the player; there is nothing to tier' },
  /**
   * What they do with the room. Like `tier`, it is worn by the *brain* rather
   * than by the fighter and is in both tables for the same reason: there is no
   * AI in the player's slot to teach, and a person looking at a screen can see
   * the pillars for themselves.
   */
  ringcraft: { who: 'player', why: 'nothing drives the player; a person reads the room with their eyes' },
  /**
   * What they wear. The player's entry has none — their gear is what they
   * bought and are wearing (`bank.worn`), and `main.js` puts it on them with
   * the spread and the charisma it comes with — so only the player skips it.
   */
  gear: { who: 'player', why: "the player's gear is the bank's, put on with what it bought" },
});

/** Whether `entry` leaves `field` behind rather than wearing it. */
export function skips(entry, field) {
  const rule = NOT_WORN[field];
  if (!rule) return false;
  return rule.who === 'anybody' || (rule.who === 'player' && Boolean(entry?.player));
}

/**
 * Each field, and the handle that wears it. **Order matters**: styles rebuild
 * the loadout, and the spread is laid over whatever the loadout came out as,
 * so a spread applied first would be thrown away by the styles that follow.
 */
export const WORN = Object.freeze([
  ['name', (entry, { fighter }) => fighter.setName?.(entry.name)],
  ['styles', (entry, { fighter }) => fighter.setStyles?.(entry.styles)],
  ['spread', (entry, { fighter }) => fighter.setSpread?.(entry.spread ?? null)],
  // Both slots wear the same mesh; this is what tells them apart. `validateLadder`
  // has already refused any opponent too close to the player's colour.
  ['tint', (entry, { fighter }) => fighter.setTint?.(entry.tint ?? null)],
  // What they wear, as the body shows it: hands, feet and shins painted from
  // the tiers' own looks. The rest of the fit is the walkout screen's.
  ['gear', (entry, { fighter }) => fighter.setGear?.(looksOf(entry.gear ?? {}))],
  ['tier', (entry, { brain }, pin) => brain?.retier?.(pin ?? entry.tier)],
  // After the tier, which empties the perception queue and clears whatever the
  // last fighter in this slot had decided to aim at.
  ['ringcraft', (entry, { brain }) => brain?.ringcraft?.(entry.ringcraft ?? null)],
  // The body. Asynchronous — the slot keeps the last body until the new one is
  // built, then swaps in one step — and last, so a slow load cannot land
  // between the other fields. Not in `ladder.json`: the runtime ladder carries
  // it from `meshes.json` (see `createLadder`).
  ['mesh', (entry, { fighter }) => fighter.wear?.(entry.mesh ?? null)],
]);

/**
 * Dresses `fighter` as `entry`, and sets `brain` to the tier they fight at.
 *
 * The same call for either slot. What the player leaves behind is decided by
 * `NOT_WORN` rather than by the caller, so there is nowhere for the two to
 * drift apart: a field added to the roster is worn by both until somebody
 * writes down, here, why one of them should not.
 *
 * `pin` overrides the tier for a headless run that wants two fighters at the
 * same difficulty regardless of who the ladder says is next. It is the only
 * way the roster's own answer is ignored, and it is a debug affordance rather
 * than a game rule.
 *
 * Returns what was applied, which is what the caller logs and the tests read.
 */
export function bookFighter(entry, { fighter, brain = null, pin = null } = {}) {
  if (!entry || !fighter) return null;

  const worn = {};
  for (const [field, apply] of WORN) {
    if (skips(entry, field)) continue;
    apply(entry, { fighter, brain }, pin);
    worn[field] = field === 'tier' ? (pin ?? entry.tier) : entry[field] ?? null;
  }

  return Object.freeze({ id: entry.id, ...worn, pinned: Boolean(pin) });
}
